import type { AxiosInstance } from "axios";
import { Check, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.js";
import { apiErrorMessage } from "../lib/api.js";
import { fetchBilling, formatPrice, startCheckout, type BillingOverview, type PlanOption } from "../lib/billing.js";

/**
 * Settings → Billing: the org's current plan and what it can move to.
 *
 * ORG-LEVEL, NOT PER STORE. One subscription covers every store an org owns; `max_stores`
 * in the plan caps them. The store picker in the header stays for context, but the plan a
 * merchant sees is their organisation's, and changing it changes it for all their stores.
 */
export function BillingScreen({
  client,
  tenantId,
}: {
  readonly client: AxiosInstance;
  /** Not sent — the org is resolved from the session. Present so switching stores refetches. */
  readonly tenantId: string;
}): React.JSX.Element {
  const [data, setData] = useState<BillingOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await fetchBilling(client));
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load your plan."));
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load, tenantId]);

  const upgrade = async (planCode: string): Promise<void> => {
    setBusy(planCode);
    try {
      const url = await startCheckout(client, planCode);
      window.location.assign(url);
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not start checkout."));
      setBusy(null);
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold">Billing</h1>
        <p className="text-muted-foreground text-sm">Your plan covers every store in this workspace.</p>
      </header>

      {error !== null && (
        <Card className="border-destructive/40">
          <CardContent className="text-destructive pt-0 text-sm">{error}</CardContent>
        </Card>
      )}

      {data === null ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <>
          <CurrentPlanCard overview={data} />
          <div className="grid gap-4 md:grid-cols-3">
            {data.plans.map((plan) => (
              <PlanCard key={plan.code} plan={plan} busy={busy === plan.code} onUpgrade={() => void upgrade(plan.code)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CurrentPlanCard({ overview }: { readonly overview: BillingOverview }): React.JSX.Element {
  const current = overview.current;
  if (current === null) {
    return (
      <Card>
        <CardContent className="text-muted-foreground pt-6 text-sm">
          You are not on a plan yet. Choose one below to start.
        </CardContent>
      </Card>
    );
  }

  /**
   * The trial banner uses the SERVER's `trialExpired`, computed at read time — never the
   * client's clock. A trial that "looks" active because the laptop's date is behind must
   * still read as expired if the server says so.
   */
  const trialNote =
    current.status === "trialing" && current.trialEndsAt !== null
      ? current.trialExpired
        ? "Your trial has ended — choose a plan to keep your reports."
        : `Trial ends ${new Date(current.trialEndsAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}.`
      : null;

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="flex items-center gap-2">
          {current.planName}
          <Badge variant={current.status === "active" ? "default" : "secondary"} className="capitalize">
            {current.status}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 text-sm">
        {trialNote !== null && (
          <p className={current.trialExpired ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}>{trialNote}</p>
        )}
        <p className="text-muted-foreground">
          {current.reportKeys.length} report{current.reportKeys.length === 1 ? "" : "s"} included
          {typeof current.limits["max_stores"] === "number" ? ` · up to ${String(current.limits["max_stores"])} stores` : ""}
        </p>
      </CardContent>
    </Card>
  );
}

function PlanCard({
  plan,
  busy,
  onUpgrade,
}: {
  readonly plan: PlanOption;
  readonly busy: boolean;
  readonly onUpgrade: () => void;
}): React.JSX.Element {
  return (
    <Card className={plan.current ? "border-primary" : undefined}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          {plan.name}
          {plan.current && (
            <Badge variant="outline" className="gap-1 border-primary text-primary">
              <Check className="size-3" /> Current
            </Badge>
          )}
        </CardTitle>
        <p className="text-2xl font-bold">
          {formatPrice(plan.priceMinor, plan.currency)}
          {plan.priceMinor > 0 && <span className="text-muted-foreground text-sm font-normal">/{plan.interval}</span>}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-muted-foreground text-sm">
          {plan.reportKeys.length} report{plan.reportKeys.length === 1 ? "" : "s"}
          {typeof plan.limits["max_stores"] === "number" ? ` · ${String(plan.limits["max_stores"])} stores` : ""}
          {plan.trialDays > 0 ? ` · ${String(plan.trialDays)}-day trial` : ""}
        </p>

        {plan.current ? (
          <Button variant="outline" size="sm" disabled>
            Your plan
          </Button>
        ) : !plan.buyable ? (
          // A priced plan with no Razorpay id cannot be checked out — say so rather than
          // offer a button that 402s at the last step.
          <Button variant="outline" size="sm" disabled title="This plan is not available for online checkout yet">
            Contact us
          </Button>
        ) : (
          <Button size="sm" disabled={busy} onClick={onUpgrade}>
            {busy && <Loader2 className="animate-spin" />}
            {plan.priceMinor === 0 ? "Switch" : "Upgrade"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
